import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Autocomplete,
  Grid,
  Chip,
  ButtonGroup,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Card,
  CardContent,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import TableChartIcon from '@mui/icons-material/TableChart';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import CalculateIcon from '@mui/icons-material/Calculate';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import {
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  Line,
} from 'recharts';
import { projectService } from '../../services/projectService';
import { projectionComparisonService } from '../../services/projectionComparisonService';
import { userService } from '../../services/userService';
import XLSX from 'xlsx-js-style';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const ProjectionComparison = () => {
  const [projects, setProjects] = useState([]);
  const [superUsers, setSuperUsers] = useState([]); // List of PDs (SuperUsers)
  const [selectedPDs, setSelectedPDs] = useState([]); // Multi-select PDs
  const [selectedProjects, setSelectedProjects] = useState([]); // Multi-select projects
  const [startMonth, setStartMonth] = useState('');
  const [endMonth, setEndMonth] = useState('');
  const [viewType, setViewType] = useState('monthly');
  const [reportType, setReportType] = useState('comparison');
  const [viewLevel, setViewLevel] = useState('employee'); // 'employee' or 'project'
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [pdProjectMap, setPdProjectMap] = useState({}); // Map PD user_id to their project codes

  useEffect(() => {
    const now = new Date();
    const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 5, 1);
    setStartMonth(formatMonthInput(sixMonthsAgo));
    setEndMonth(formatMonthInput(now));
  }, []);

  useEffect(() => {
    loadProjects();
    loadSuperUsers();
  }, []);

  const formatMonthInput = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return year + '-' + month;
  };

  const loadProjects = async () => {
    try {
      const data = await projectService.getAll();
      setProjects(data || []);
    } catch (err) {
      console.error('Failed to load projects:', err);
      setError('Failed to load projects');
    }
  };

  const loadSuperUsers = async () => {
    try {
      const users = await userService.getSuperUsers();
      setSuperUsers(users || []);

      // Build PD to project mapping
      const pdMap = {};
      for (const user of users || []) {
        if (user.user_id) {
          try {
            const userDetails = await userService.getById(user.user_id);
            pdMap[user.user_id] = (userDetails?.projects || []).map((p) => p.project_code);
          } catch (e) {
            pdMap[user.user_id] = [];
          }
        }
      }
      setPdProjectMap(pdMap);
    } catch (err) {
      console.error('Failed to load SuperUsers:', err);
    }
  };

  // Filter projects based on selected PDs
  const filteredProjects = React.useMemo(() => {
    if (selectedPDs.length === 0) return projects;

    const pdProjectCodes = new Set();
    selectedPDs.forEach((pd) => {
      const codes = pdProjectMap[pd.user_id] || [];
      codes.forEach((code) => pdProjectCodes.add(code));
    });

    return projects.filter((p) => pdProjectCodes.has(p.project_code));
  }, [projects, selectedPDs, pdProjectMap]);

  // Get effective project codes based on PD and project selection
  const getEffectiveProjectCodes = () => {
    // If specific projects are selected, use those
    if (selectedProjects.length > 0) {
      return selectedProjects.map((p) => p.project_code);
    }
    // If PDs are selected but no specific projects, use filtered projects (PD's projects)
    if (selectedPDs.length > 0 && filteredProjects.length > 0) {
      return filteredProjects.map((p) => p.project_code);
    }
    // Otherwise return null for ALL
    return null;
  };

  const handleGenerateProjections = async () => {
    if (!startMonth || !endMonth) {
      setError('Please select start and end months');
      return;
    }
    if (startMonth > endMonth) {
      setError('Start month must be before or equal to end month');
      return;
    }
    try {
      setGenerating(true);
      setError('');
      setSuccessMessage('');
      // Use selected projects, or PD's projects, or ALL
      const effectiveCodes = getEffectiveProjectCodes();
      const projectCodes = effectiveCodes ? effectiveCodes.join(',') : 'ALL';
      const result = await projectionComparisonService.generateProjections(
        projectCodes,
        startMonth,
        endMonth
      );
      setSuccessMessage(result.message);
    } catch (err) {
      console.error('Failed to generate projections:', err);
      setError(err.response?.data?.error || 'Failed to generate projections');
    } finally {
      setGenerating(false);
    }
  };

  const handleLoadReport = async () => {
    if (!startMonth || !endMonth) {
      setError('Please select start and end months');
      return;
    }
    if (startMonth > endMonth) {
      setError('Start month must be before or equal to end month');
      return;
    }
    try {
      setLoading(true);
      setError('');
      setSuccessMessage('');
      // Use selected projects, or PD's projects, or ALL
      const effectiveCodes = getEffectiveProjectCodes();
      const projectsToQuery = effectiveCodes || 'ALL';
      const response = await projectionComparisonService.getReport(
        projectsToQuery,
        startMonth,
        endMonth,
        viewType,
        reportType,
        viewLevel
      );
      setReportData(response);
    } catch (err) {
      console.error('Failed to load report:', err);
      setError(err.response?.data?.error || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const formatMonthDisplay = (monthStr) => {
    if (!monthStr) return '';

    // Handle quarterly format (e.g., "Q1-25 Jan-Mar")
    if (typeof monthStr === 'string' && monthStr.startsWith('Q')) {
      return monthStr;
    }

    // Handle yearly format (e.g., "2025 Jan-Dec")
    if (typeof monthStr === 'string' && /^\d{4}\s/.test(monthStr)) {
      return monthStr;
    }

    // Handle bare year format (e.g., "2025") - backend sends just the year for yearly view
    if (typeof monthStr === 'string' && /^\d{4}$/.test(monthStr)) {
      return monthStr + ' Jan-Dec';
    }

    // Handle monthly format (e.g., "2024-01-01")
    try {
      const date = new Date(monthStr);
      // Check both isNaN and Invalid Date string
      if (isNaN(date.getTime()) || date.toString() === 'Invalid Date') {
        return monthStr; // Return as-is if not a valid date
      }
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
    } catch (e) {
      return monthStr; // Return as-is on any error
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '-';
    if (value === 0) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined) return '-';
    const sign = value >= 0 ? '+' : '';
    return sign + value.toFixed(1) + '%';
  };

  const getVarianceColor = (variance, variancePercent) => {
    if (variance === null || variance === undefined) return '#9E9E9E';
    if (variance < 0) return '#E57373'; // Red - actual exceeds projection (negative variance)
    return '#66BB6A'; // Green - actual is at or below projection (positive variance)
  };

  const getChartData = () => {
    if (!reportData || !reportData.aggregated) return [];
    return reportData.aggregated.periods.map((p) => ({
      name: p.period,
      projection: p.projection,
      actual: p.actual,
      variance: p.variance,
      variancePercent: p.variancePercent,
    }));
  };

  // Get unique months from data for table headers
  const getMonths = () => {
    if (!reportData) return [];
    // Check project-level data first if in project view
    if (viewLevel === 'project' && reportData.projects && reportData.projects.length > 0) {
      return reportData.projects[0].monthly_comparison.map((c) => c.month);
    }
    // Fall back to employee data
    if (reportData.employees && reportData.employees.length > 0) {
      return reportData.employees[0].monthly_comparison.map((c) => c.month);
    }
    return [];
  };

  // Export to Excel
  const handleExportExcel = () => {
    if (!reportData) return;

    const months = getMonths();
    const isComparison = reportType === 'comparison';
    const isEmployeeView = viewLevel === 'employee';
    const dataSource = isEmployeeView ? reportData.employees : reportData.projects;

    // Build headers based on view level
    const headers = isEmployeeView
      ? ['SNo', 'Employee ID', 'Employee Name', 'PD', 'PM', 'Rate']
      : ['SNo', 'Project Code', 'Project Name', 'PD', 'PM'];

    if (isComparison) {
      months.forEach((month) => {
        headers.push(formatMonthDisplay(month) + ' Proj');
        headers.push(formatMonthDisplay(month) + ' Act');
        headers.push(formatMonthDisplay(month) + ' Var%');
      });
    } else {
      months.forEach((month) => {
        headers.push(formatMonthDisplay(month));
      });
    }
    headers.push('Total Projection');
    if (isComparison) {
      headers.push('Total Actual');
      headers.push('Total Variance%');
    }

    // Build header row with styling
    const headerRow = headers.map((header) => ({
      v: header,
      t: 's',
      s: {
        font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 10 },
        fill: { fgColor: { rgb: '607D8B' } },
        alignment: { horizontal: 'center', vertical: 'center' },
        border: {
          top: { style: 'thin', color: { rgb: 'E0E0E0' } },
          bottom: { style: 'thin', color: { rgb: 'E0E0E0' } },
          left: { style: 'thin', color: { rgb: 'E0E0E0' } },
          right: { style: 'thin', color: { rgb: 'E0E0E0' } },
        },
      },
    }));

    // Build data rows
    const dataRows = dataSource.map((item, rowIndex) => {
      const bgColor = rowIndex % 2 === 0 ? 'FAFAFA' : 'FFFFFF';
      const rowData = isEmployeeView
        ? [rowIndex + 1, item.emp_display_id, item.name, item.pd_name || '-', item.pm_name || '-', item.bill_rate]
        : [rowIndex + 1, item.project_code, item.project_name, item.pd_name || '-', item.pm_name || '-'];

      if (isComparison) {
        item.monthly_comparison.forEach((comp) => {
          rowData.push(comp.projection);
          rowData.push(comp.actual ?? 0);
          rowData.push(comp.variancePercent !== null ? comp.variancePercent.toFixed(1) + '%' : '-');
        });
      } else {
        item.monthly_comparison.forEach((comp) => {
          rowData.push(comp.projection);
        });
      }

      rowData.push(item.totals.projection);
      if (isComparison) {
        rowData.push(item.totals.actual);
        rowData.push(item.totals.variancePercent.toFixed(1) + '%');
      }

      const numStartCol = isEmployeeView ? 5 : 5;
      return rowData.map((cell, colIndex) => {
        const isNumber = colIndex >= numStartCol && typeof cell === 'number';
        return {
          v: cell,
          t: isNumber ? 'n' : 's',
          s: {
            font: { sz: 9 },
            fill: { fgColor: { rgb: bgColor } },
            alignment: { horizontal: isNumber ? 'right' : 'left', vertical: 'center' },
            numFmt: isNumber ? '$#,##0' : undefined,
            border: {
              top: { style: 'thin', color: { rgb: 'E0E0E0' } },
              bottom: { style: 'thin', color: { rgb: 'E0E0E0' } },
              left: { style: 'thin', color: { rgb: 'E0E0E0' } },
              right: { style: 'thin', color: { rgb: 'E0E0E0' } },
            },
          },
        };
      });
    });

    // Build totals row
    const totalsRowData = isEmployeeView
      ? ['', 'TOTAL', '', '', '', '']
      : ['', 'TOTAL', '', '', ''];

    if (isComparison) {
      reportData.aggregated.periods.forEach((p) => {
        totalsRowData.push(p.projection);
        totalsRowData.push(p.actual);
        totalsRowData.push(p.variancePercent !== null ? p.variancePercent.toFixed(1) + '%' : '-');
      });
    } else {
      reportData.aggregated.periods.forEach((p) => {
        totalsRowData.push(p.projection);
      });
    }
    totalsRowData.push(reportData.aggregated.totals.projection);
    if (isComparison) {
      totalsRowData.push(reportData.aggregated.totals.actual);
      totalsRowData.push(reportData.aggregated.totals.variancePercent.toFixed(1) + '%');
    }

    const numStartCol = isEmployeeView ? 5 : 5;
    const totalsRow = totalsRowData.map((cell, colIndex) => {
      const isNumber = colIndex >= numStartCol && typeof cell === 'number';
      return {
        v: cell,
        t: isNumber ? 'n' : 's',
        s: {
          font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 10 },
          fill: { fgColor: { rgb: '546E7A' } },
          alignment: { horizontal: isNumber ? 'right' : 'left', vertical: 'center' },
          numFmt: isNumber ? '$#,##0' : undefined,
          border: {
            top: { style: 'thin', color: { rgb: 'E0E0E0' } },
            bottom: { style: 'thin', color: { rgb: 'E0E0E0' } },
            left: { style: 'thin', color: { rgb: 'E0E0E0' } },
            right: { style: 'thin', color: { rgb: 'E0E0E0' } },
          },
        },
      };
    });

    const wsData = [headerRow, ...dataRows, totalsRow];
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Set column widths
    const colWidths = isEmployeeView
      ? [{ wch: 5 }, { wch: 12 }, { wch: 20 }, { wch: 12 }, { wch: 12 }, { wch: 8 }]
      : [{ wch: 5 }, { wch: 12 }, { wch: 25 }, { wch: 12 }, { wch: 12 }];

    const monthColWidth = isComparison ? 10 : 12;
    months.forEach(() => {
      if (isComparison) {
        colWidths.push({ wch: monthColWidth });
        colWidths.push({ wch: monthColWidth });
        colWidths.push({ wch: 8 });
      } else {
        colWidths.push({ wch: monthColWidth });
      }
    });
    colWidths.push({ wch: 14 });
    if (isComparison) {
      colWidths.push({ wch: 12 });
      colWidths.push({ wch: 10 });
    }
    ws['!cols'] = colWidths;

    const wb = XLSX.utils.book_new();
    const sheetName = isEmployeeView ? 'Employee Comparison' : 'Project Comparison';
    XLSX.utils.book_append_sheet(wb, ws, sheetName);

    const fileName = 'Projection_Comparison_' + startMonth + '_to_' + endMonth + '.xlsx';
    XLSX.writeFile(wb, fileName);
  };

  // Export to PDF
  const handleExportPDF = () => {
    if (!reportData) return;

    const months = getMonths();
    const isComparison = reportType === 'comparison';
    const isEmployeeView = viewLevel === 'employee';
    const dataSource = isEmployeeView ? reportData.employees : reportData.projects;

    const doc = new jsPDF('landscape', 'mm', 'a4');

    // Title
    doc.setFontSize(16);
    doc.setTextColor(69, 90, 100);
    doc.text('Projection vs. Actual Comparison (' + (isEmployeeView ? 'Employee' : 'Project') + ' View)', 14, 15);

    // Subtitle
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(
      'Period: ' + formatMonthDisplay(startMonth + '-01') + ' to ' + formatMonthDisplay(endMonth + '-01'),
      14,
      22
    );

    // Build headers based on view level
    const headers = isEmployeeView
      ? [['#', 'Emp ID', 'Name', 'PD', 'PM', 'Rate']]
      : [['#', 'Code', 'Project', 'PD', 'PM']];

    if (isComparison) {
      months.forEach((month) => {
        headers[0].push(formatMonthDisplay(month) + ' P');
        headers[0].push(formatMonthDisplay(month) + ' A');
      });
    } else {
      months.forEach((month) => {
        headers[0].push(formatMonthDisplay(month));
      });
    }
    headers[0].push('Total');

    // Build data
    const data = dataSource.map((item, index) => {
      const rowData = isEmployeeView
        ? [index + 1, item.emp_display_id, item.name, item.pd_name || '-', item.pm_name || '-', '$' + item.bill_rate]
        : [index + 1, item.project_code, item.project_name, item.pd_name || '-', item.pm_name || '-'];

      if (isComparison) {
        item.monthly_comparison.forEach((comp) => {
          rowData.push(formatCurrency(comp.projection));
          rowData.push(formatCurrency(comp.actual));
        });
      } else {
        item.monthly_comparison.forEach((comp) => {
          rowData.push(formatCurrency(comp.projection));
        });
      }
      rowData.push(formatCurrency(item.totals.projection));
      return rowData;
    });

    // Add totals row
    const totalsRow = isEmployeeView
      ? ['', 'TOTAL', '', '', '', '']
      : ['', 'TOTAL', '', '', ''];

    if (isComparison) {
      reportData.aggregated.periods.forEach((p) => {
        totalsRow.push(formatCurrency(p.projection));
        totalsRow.push(formatCurrency(p.actual));
      });
    } else {
      reportData.aggregated.periods.forEach((p) => {
        totalsRow.push(formatCurrency(p.projection));
      });
    }
    totalsRow.push(formatCurrency(reportData.aggregated.totals.projection));
    data.push(totalsRow);

    const totalRows = data.length;

    const columnStyles = isEmployeeView
      ? {
          0: { halign: 'center', cellWidth: 8 },
          1: { cellWidth: 18 },
          2: { cellWidth: 22 },
          3: { cellWidth: 15 },
          4: { cellWidth: 15 },
          5: { halign: 'right', cellWidth: 12 },
        }
      : {
          0: { halign: 'center', cellWidth: 8 },
          1: { cellWidth: 18 },
          2: { cellWidth: 30 },
          3: { cellWidth: 15 },
          4: { cellWidth: 15 },
        };

    const numStartCol = isEmployeeView ? 5 : 4;

    autoTable(doc, {
      head: headers,
      body: data,
      startY: 28,
      styles: {
        fontSize: 7,
        cellPadding: 1.5,
        lineColor: [200, 200, 200],
        lineWidth: 0.1,
      },
      headStyles: {
        fillColor: [96, 125, 139],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        halign: 'center',
      },
      columnStyles: columnStyles,
      didParseCell: function (hookData) {
        if (hookData.column.index > numStartCol) {
          hookData.cell.styles.halign = 'right';
        }
        // Alternate row colors
        if (hookData.section === 'body' && hookData.row.index < totalRows - 1) {
          if (hookData.row.index % 2 === 0) {
            hookData.cell.styles.fillColor = [250, 250, 250];
          } else {
            hookData.cell.styles.fillColor = [255, 255, 255];
          }
        }
        // Totals row styling
        if (hookData.section === 'body' && hookData.row.index === totalRows - 1) {
          hookData.cell.styles.fillColor = [84, 110, 122];
          hookData.cell.styles.textColor = [255, 255, 255];
          hookData.cell.styles.fontStyle = 'bold';
        }
      },
    });

    const fileName = 'Projection_Comparison_' + startMonth + '_to_' + endMonth + '.pdf';
    doc.save(fileName);
  };

  const evenRowColor = '#F5F7F9';
  const oddRowColor = '#FFFFFF';

  return (
    <Container maxWidth="xl">
      <Box sx={{ mt: 3, mb: 4 }}>
        {/* Header Section */}
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            px: 2,
            borderRadius: 2,
            background: '#E8EEF2',
            color: '#37474F',
            border: '1px solid #CFD8DC',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CompareArrowsIcon sx={{ fontSize: 28 }} />
            <Box>
              <Typography variant="h6" fontWeight="bold">
                Projection vs. Actual Comparison
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                Compare projected billing (20-day standard) against actual billing values
              </Typography>
            </Box>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSuccessMessage('')}>
            {successMessage}
          </Alert>
        )}

        {/* Filter Section */}
        <Paper
          sx={{
            p: 3,
            mb: 3,
            borderRadius: 3,
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            border: '1px solid #E8EAF6',
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={2}>
              <Autocomplete
                multiple
                options={superUsers}
                getOptionLabel={(option) => option.username || ''}
                value={selectedPDs}
                onChange={(event, newValue) => {
                  setSelectedPDs(newValue);
                  // Reset project selection when PD changes
                  setSelectedProjects([]);
                }}
                isOptionEqualToValue={(option, value) => option.user_id === value?.user_id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="PD"
                    size="small"
                    placeholder={selectedPDs.length === 0 ? 'All PDs' : ''}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.user_id}
                      label={option.username}
                      size="small"
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  ))
                }
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Autocomplete
                multiple
                options={filteredProjects}
                getOptionLabel={(option) => option.name + ' (' + option.project_code + ')'}
                value={selectedProjects}
                onChange={(event, newValue) => {
                  setSelectedProjects(newValue);
                }}
                isOptionEqualToValue={(option, value) => option.project_code === value?.project_code}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Project"
                    size="small"
                    placeholder={selectedProjects.length === 0 ? 'All Projects' : ''}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.project_code}
                      label={option.project_code}
                      size="small"
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  ))
                }
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                label="Start Month"
                type="month"
                value={startMonth}
                onChange={(e) => setStartMonth(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                label="End Month"
                type="month"
                value={endMonth}
                onChange={(e) => setEndMonth(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  startIcon={generating ? <CircularProgress size={18} color="inherit" /> : <CalculateIcon />}
                  onClick={handleGenerateProjections}
                  disabled={generating || loading}
                  sx={{
                    borderRadius: 2,
                    borderColor: '#78909C',
                    color: '#546E7A',
                    '&:hover': {
                      backgroundColor: '#ECEFF1',
                      borderColor: '#607D8B',
                    },
                  }}
                >
                  {generating ? 'Generating...' : 'Generate Projections'}
                </Button>
                <Button
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
                  onClick={handleLoadReport}
                  disabled={loading || generating}
                  sx={{
                    borderRadius: 2,
                    backgroundColor: '#607D8B',
                    boxShadow: '0 2px 8px rgba(96, 125, 139, 0.3)',
                    '&:hover': {
                      backgroundColor: '#546E7A',
                    },
                  }}
                >
                  {loading ? 'Loading...' : 'Load Report'}
                </Button>
              </Box>
            </Grid>
          </Grid>

          {/* Report Type, View Type, and Grid View */}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={4}>
              <FormControl component="fieldset">
                <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5 }}>
                  Report Type
                </Typography>
                <RadioGroup
                  row
                  value={reportType}
                  onChange={(e) => setReportType(e.target.value)}
                >
                  <FormControlLabel
                    value="comparison"
                    control={<Radio size="small" />}
                    label={<Typography variant="body2">Comparison</Typography>}
                  />
                  <FormControlLabel
                    value="projection_only"
                    control={<Radio size="small" />}
                    label={<Typography variant="body2">Projection Only</Typography>}
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl component="fieldset">
                <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5 }}>
                  View Type
                </Typography>
                <RadioGroup row value={viewType} onChange={(e) => setViewType(e.target.value)}>
                  <FormControlLabel
                    value="monthly"
                    control={<Radio size="small" />}
                    label={<Typography variant="body2">Monthly</Typography>}
                  />
                  <FormControlLabel
                    value="quarterly"
                    control={<Radio size="small" />}
                    label={<Typography variant="body2">Quarterly</Typography>}
                  />
                  <FormControlLabel
                    value="yearly"
                    control={<Radio size="small" />}
                    label={<Typography variant="body2">Yearly</Typography>}
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl component="fieldset">
                <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5 }}>
                  Grid View
                </Typography>
                <RadioGroup row value={viewLevel} onChange={(e) => setViewLevel(e.target.value)}>
                  <FormControlLabel
                    value="employee"
                    control={<Radio size="small" />}
                    label={<Typography variant="body2">Employee</Typography>}
                  />
                  <FormControlLabel
                    value="project"
                    control={<Radio size="small" />}
                    label={<Typography variant="body2">Project</Typography>}
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              {reportData && (
                <ButtonGroup variant="outlined" fullWidth sx={{ borderRadius: 2 }}>
                  <Button
                    startIcon={<TableChartIcon />}
                    onClick={handleExportExcel}
                    sx={{
                      borderRadius: '8px 0 0 8px',
                      color: '#607D8B',
                      borderColor: '#B0BEC5',
                      '&:hover': {
                        backgroundColor: '#ECEFF1',
                        borderColor: '#90A4AE',
                      },
                    }}
                  >
                    Excel
                  </Button>
                  <Button
                    startIcon={<PictureAsPdfIcon />}
                    onClick={handleExportPDF}
                    sx={{
                      borderRadius: '0 8px 8px 0',
                      color: '#607D8B',
                      borderColor: '#B0BEC5',
                      '&:hover': {
                        backgroundColor: '#ECEFF1',
                        borderColor: '#90A4AE',
                      },
                    }}
                  >
                    PDF
                  </Button>
                </ButtonGroup>
              )}
            </Grid>
          </Grid>
        </Paper>

        {/* Summary Cards */}
        {reportData && reportData.aggregated && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  borderRadius: 2,
                  backgroundColor: '#E8F1F8',
                  border: '1px solid #C5D9E8',
                  boxShadow: 'none',
                }}
              >
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="caption" color="textSecondary">
                    Total Projection
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" sx={{ color: '#4A7BA7' }}>
                    {formatCurrency(reportData.aggregated.totals.projection)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            {reportType === 'comparison' && (
              <>
                <Grid item xs={12} sm={6} md={3}>
                  <Card
                    sx={{
                      borderRadius: 2,
                      backgroundColor: '#E8F3F1',
                      border: '1px solid #C5DDD8',
                      boxShadow: 'none',
                    }}
                  >
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <Typography variant="caption" color="textSecondary">
                        Total Actual
                      </Typography>
                      <Typography variant="h5" fontWeight="bold" sx={{ color: '#4A8F7D' }}>
                        {formatCurrency(reportData.aggregated.totals.actual)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card
                    sx={{
                      borderRadius: 2,
                      backgroundColor: '#F5F7FA',
                      border: '1px solid #E0E0E0',
                      boxShadow: 'none',
                    }}
                  >
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <Typography variant="caption" color="textSecondary">
                        Variance
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        {reportData.aggregated.totals.variance >= 0 ? (
                          <TrendingUpIcon sx={{ color: '#66BB6A' }} />
                        ) : (
                          <TrendingDownIcon sx={{ color: '#E57373' }} />
                        )}
                        <Typography
                          variant="h5"
                          fontWeight="bold"
                          sx={{
                            color: reportData.aggregated.totals.variance < 0 ? '#E57373' : '#66BB6A',
                          }}
                        >
                          {formatCurrency(reportData.aggregated.totals.variance)}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card
                    sx={{
                      borderRadius: 2,
                      backgroundColor: '#F5F7FA',
                      border: '1px solid #E0E0E0',
                      boxShadow: 'none',
                    }}
                  >
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <Typography variant="caption" color="textSecondary">
                        Variance %
                      </Typography>
                      <Typography
                        variant="h5"
                        fontWeight="bold"
                        sx={{
                          color: reportData.aggregated.totals.variancePercent < 0 ? '#E57373' : '#66BB6A',
                        }}
                      >
                        {formatPercent(reportData.aggregated.totals.variancePercent)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </>
            )}
          </Grid>
        )}

        {/* Chart Section */}
        {reportData && reportData.aggregated && reportData.aggregated.periods.length > 0 && (
          <Paper
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            }}
          >
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: '#475569' }}>
              {reportType === 'comparison' ? 'Projection vs Actual' : 'Projection Values'}
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={getChartData()}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis
                  tickFormatter={(value) => '$' + (value / 1000).toFixed(0) + 'k'}
                  tick={{ fontSize: 12 }}
                />
                <Tooltip
                  formatter={(value, name) => {
                    if (name === 'variancePercent') return formatPercent(value);
                    return formatCurrency(value);
                  }}
                  labelStyle={{ fontWeight: 'bold' }}
                />
                <Legend />
                <Bar dataKey="projection" name="Projection" fill="#5C9BD1" radius={[4, 4, 0, 0]} />
                {reportType === 'comparison' && (
                  <Bar dataKey="actual" name="Actual" fill="#66A696" radius={[4, 4, 0, 0]} />
                )}
                {reportType === 'comparison' && (
                  <Line
                    type="monotone"
                    dataKey="variancePercent"
                    name="Variance %"
                    stroke="#8D9CA8"
                    strokeWidth={2}
                    yAxisId={1}
                    dot={{ fill: '#7A8B97', strokeWidth: 2 }}
                  />
                )}
                {reportType === 'comparison' && (
                  <YAxis
                    yAxisId={1}
                    orientation="right"
                    tickFormatter={(value) => value.toFixed(0) + '%'}
                    tick={{ fontSize: 12 }}
                  />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </Paper>
        )}

        {/* Data Table */}
        {reportData && ((viewLevel === 'employee' && reportData.employees && reportData.employees.length > 0) ||
          (viewLevel === 'project' && reportData.projects && reportData.projects.length > 0)) && (
          <Paper
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            }}
          >
            <Box
              sx={{
                p: 2,
                background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8f0 100%)',
                borderBottom: '1px solid #E0E0E0',
                display: 'flex',
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              <Chip
                label={viewLevel === 'employee'
                  ? 'Employees: ' + reportData.employees.length
                  : 'Projects: ' + reportData.projects.length}
                sx={{
                  backgroundColor: '#ECEFF1',
                  color: '#546E7A',
                  fontWeight: 600,
                }}
              />
              <Chip
                label={'Period: ' + formatMonthDisplay(reportData.period.start) + ' - ' + formatMonthDisplay(reportData.period.end)}
                sx={{
                  backgroundColor: '#ECEFF1',
                  color: '#546E7A',
                  fontWeight: 600,
                }}
              />
            </Box>

            <TableContainer sx={{ maxHeight: 'calc(100vh - 400px)', overflow: 'auto' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        backgroundColor: '#607D8B',
                        color: 'white',
                        fontWeight: 'bold',
                        position: 'sticky',
                        left: 0,
                        zIndex: 3,
                        minWidth: 50,
                        borderBottom: 'none',
                      }}
                      align="center"
                    >
                      #
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: '#607D8B',
                        color: 'white',
                        fontWeight: 'bold',
                        position: 'sticky',
                        left: 50,
                        zIndex: 3,
                        minWidth: 100,
                        borderBottom: 'none',
                      }}
                    >
                      {viewLevel === 'employee' ? 'Employee ID' : 'Project Code'}
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: '#607D8B',
                        color: 'white',
                        fontWeight: 'bold',
                        position: 'sticky',
                        left: 150,
                        zIndex: 3,
                        minWidth: 150,
                        borderBottom: 'none',
                      }}
                    >
                      {viewLevel === 'employee' ? 'Name' : 'Project Name'}
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: '#607D8B',
                        color: 'white',
                        fontWeight: 'bold',
                        minWidth: 80,
                        borderBottom: 'none',
                      }}
                    >
                      PD
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: '#607D8B',
                        color: 'white',
                        fontWeight: 'bold',
                        minWidth: 80,
                        borderBottom: 'none',
                      }}
                    >
                      PM
                    </TableCell>
                    {viewLevel === 'employee' && (
                      <TableCell
                        sx={{
                          backgroundColor: '#607D8B',
                          color: 'white',
                          fontWeight: 'bold',
                          minWidth: 80,
                          borderBottom: 'none',
                        }}
                        align="right"
                      >
                        Rate
                      </TableCell>
                    )}
                    {getMonths().map((month, index) => (
                      <React.Fragment key={month}>
                        <TableCell
                          sx={{
                            backgroundColor: '#5C9BD1',
                            color: 'white',
                            fontWeight: 'bold',
                            minWidth: 90,
                            borderBottom: '2px solid #4A89C0',
                            fontSize: '0.75rem',
                          }}
                          align="right"
                        >
                          {formatMonthDisplay(month)}
                          <br />
                          <span style={{ fontSize: '0.65rem', opacity: 0.9 }}>Proj</span>
                        </TableCell>
                        {reportType === 'comparison' && (
                          <>
                            <TableCell
                              sx={{
                                backgroundColor: '#66A696',
                                color: 'white',
                                fontWeight: 'bold',
                                minWidth: 90,
                                borderBottom: '2px solid #549485',
                                fontSize: '0.75rem',
                              }}
                              align="right"
                            >
                              {formatMonthDisplay(month)}
                              <br />
                              <span style={{ fontSize: '0.65rem', opacity: 0.9 }}>Act</span>
                            </TableCell>
                            <TableCell
                              sx={{
                                backgroundColor: '#8D9CA8',
                                color: 'white',
                                fontWeight: 'bold',
                                minWidth: 60,
                                borderBottom: '2px solid #7A8B97',
                                fontSize: '0.7rem',
                              }}
                              align="center"
                            >
                              Var%
                            </TableCell>
                          </>
                        )}
                      </React.Fragment>
                    ))}
                    <TableCell
                      sx={{
                        backgroundColor: '#78909C',
                        color: 'white',
                        fontWeight: 'bold',
                        minWidth: 100,
                        borderBottom: '2px solid #607D8B',
                      }}
                      align="right"
                    >
                      Total Proj
                    </TableCell>
                    {reportType === 'comparison' && (
                      <>
                        <TableCell
                          sx={{
                            backgroundColor: '#78909C',
                            color: 'white',
                            fontWeight: 'bold',
                            minWidth: 100,
                            borderBottom: '2px solid #607D8B',
                          }}
                          align="right"
                        >
                          Total Act
                        </TableCell>
                        <TableCell
                          sx={{
                            backgroundColor: '#78909C',
                            color: 'white',
                            fontWeight: 'bold',
                            minWidth: 80,
                            borderBottom: '2px solid #607D8B',
                          }}
                          align="center"
                        >
                          Var%
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(viewLevel === 'employee' ? reportData.employees : reportData.projects).map((item, rowIndex) => (
                    <TableRow
                      key={viewLevel === 'employee' ? `${item.emp_id}_${item.project_code}` : item.project_code}
                      sx={{
                        backgroundColor: rowIndex % 2 === 0 ? evenRowColor : oddRowColor,
                        '&:hover': {
                          backgroundColor: '#ECEFF1 !important',
                          '& td': { backgroundColor: '#ECEFF1 !important' },
                        },
                        transition: 'background-color 0.2s',
                      }}
                    >
                      <TableCell
                        align="center"
                        sx={{
                          position: 'sticky',
                          left: 0,
                          backgroundColor: rowIndex % 2 === 0 ? evenRowColor : oddRowColor,
                          zIndex: 1,
                          borderBottom: '1px solid #E0E0E0',
                        }}
                      >
                        {rowIndex + 1}
                      </TableCell>
                      <TableCell
                        sx={{
                          position: 'sticky',
                          left: 50,
                          backgroundColor: rowIndex % 2 === 0 ? evenRowColor : oddRowColor,
                          zIndex: 1,
                          fontWeight: 'bold',
                          color: '#546E7A',
                          borderBottom: '1px solid #E0E0E0',
                        }}
                      >
                        {viewLevel === 'employee' ? item.emp_display_id : item.project_code}
                      </TableCell>
                      <TableCell
                        sx={{
                          position: 'sticky',
                          left: 150,
                          backgroundColor: rowIndex % 2 === 0 ? evenRowColor : oddRowColor,
                          zIndex: 1,
                          borderBottom: '1px solid #E0E0E0',
                          fontWeight: 500,
                        }}
                      >
                        {viewLevel === 'employee' ? item.name : item.project_name}
                      </TableCell>
                      <TableCell
                        sx={{
                          borderBottom: '1px solid #E0E0E0',
                          color: '#475569',
                          fontSize: '0.75rem',
                        }}
                      >
                        {item.pd_name || '-'}
                      </TableCell>
                      <TableCell
                        sx={{
                          borderBottom: '1px solid #E0E0E0',
                          color: '#475569',
                          fontSize: '0.75rem',
                        }}
                      >
                        {item.pm_name || '-'}
                      </TableCell>
                      {viewLevel === 'employee' && (
                        <TableCell
                          align="right"
                          sx={{
                            borderBottom: '1px solid #E0E0E0',
                            color: '#475569',
                          }}
                        >
                          ${item.bill_rate}
                        </TableCell>
                      )}
                      {item.monthly_comparison.map((comp, monthIdx) => (
                        <React.Fragment key={comp.month}>
                          <TableCell
                            align="right"
                            sx={{
                              borderBottom: '1px solid #E0E0E0',
                              color: '#37474F',
                              fontWeight: 500,
                              backgroundColor:
                                rowIndex % 2 === 0
                                  ? '#E8F1F8'
                                  : '#F5FAFD',
                            }}
                          >
                            {formatCurrency(comp.projection)}
                          </TableCell>
                          {reportType === 'comparison' && (
                            <>
                              <TableCell
                                align="right"
                                sx={{
                                  borderBottom: '1px solid #E0E0E0',
                                  color: '#37474F',
                                  fontWeight: 500,
                                  backgroundColor:
                                    rowIndex % 2 === 0
                                      ? '#E8F3F1'
                                      : '#F5FAF9',
                                }}
                              >
                                {formatCurrency(comp.actual)}
                              </TableCell>
                              <TableCell
                                align="center"
                                sx={{
                                  borderBottom: '1px solid #E0E0E0',
                                  color: getVarianceColor(comp.variance, comp.variancePercent),
                                  fontWeight: 600,
                                  fontSize: '0.75rem',
                                }}
                              >
                                {formatPercent(comp.variancePercent)}
                              </TableCell>
                            </>
                          )}
                        </React.Fragment>
                      ))}
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: 'bold',
                          backgroundColor: rowIndex % 2 === 0 ? '#ECEFF1' : '#F5F5F5',
                          color: '#455A64',
                          borderBottom: '1px solid #E0E0E0',
                        }}
                      >
                        {formatCurrency(item.totals.projection)}
                      </TableCell>
                      {reportType === 'comparison' && (
                        <>
                          <TableCell
                            align="right"
                            sx={{
                              fontWeight: 'bold',
                              backgroundColor: rowIndex % 2 === 0 ? '#ECEFF1' : '#F5F5F5',
                              color: '#455A64',
                              borderBottom: '1px solid #E0E0E0',
                            }}
                          >
                            {formatCurrency(item.totals.actual)}
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              fontWeight: 'bold',
                              color: getVarianceColor(item.totals.variance, item.totals.variancePercent),
                              borderBottom: '1px solid #E0E0E0',
                            }}
                          >
                            {formatPercent(item.totals.variancePercent)}
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}
                  {/* Grand Totals Row */}
                  <TableRow
                    sx={{
                      backgroundColor: '#607D8B',
                    }}
                  >
                    <TableCell
                      colSpan={viewLevel === 'employee' ? 6 : 5}
                      sx={{
                        fontWeight: 'bold',
                        fontSize: '1rem',
                        position: 'sticky',
                        left: 0,
                        backgroundColor: '#607D8B',
                        color: 'white',
                        zIndex: 1,
                        borderBottom: 'none',
                      }}
                    >
                      GRAND TOTAL
                    </TableCell>
                    {reportData.aggregated.periods.map((period) => (
                      <React.Fragment key={period.periodKey}>
                        <TableCell
                          align="right"
                          sx={{ fontWeight: 'bold', color: 'white', borderBottom: 'none' }}
                        >
                          {formatCurrency(period.projection)}
                        </TableCell>
                        {reportType === 'comparison' && (
                          <>
                            <TableCell
                              align="right"
                              sx={{ fontWeight: 'bold', color: 'white', borderBottom: 'none' }}
                            >
                              {formatCurrency(period.actual)}
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{ fontWeight: 'bold', color: 'white', borderBottom: 'none', fontSize: '0.75rem' }}
                            >
                              {formatPercent(period.variancePercent)}
                            </TableCell>
                          </>
                        )}
                      </React.Fragment>
                    ))}
                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: 'bold',
                        backgroundColor: '#546E7A',
                        color: 'white',
                        borderBottom: 'none',
                      }}
                    >
                      {formatCurrency(reportData.aggregated.totals.projection)}
                    </TableCell>
                    {reportType === 'comparison' && (
                      <>
                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: 'bold',
                            backgroundColor: '#546E7A',
                            color: 'white',
                            borderBottom: 'none',
                          }}
                        >
                          {formatCurrency(reportData.aggregated.totals.actual)}
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            fontWeight: 'bold',
                            backgroundColor: '#546E7A',
                            color: 'white',
                            borderBottom: 'none',
                          }}
                        >
                          {formatPercent(reportData.aggregated.totals.variancePercent)}
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {reportData && (
          (viewLevel === 'employee' && reportData.employees && reportData.employees.length === 0) ||
          (viewLevel === 'project' && reportData.projects && reportData.projects.length === 0)
        ) && (
          <Paper
            sx={{
              p: 6,
              textAlign: 'center',
              borderRadius: 3,
              background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8f0 100%)',
            }}
          >
            <Typography color="textSecondary" variant="h6">
              No projection data found. Click "Generate Projections" first.
            </Typography>
          </Paper>
        )}

        {!reportData && !loading && (
          <Paper
            sx={{
              p: 6,
              textAlign: 'center',
              borderRadius: 3,
              background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8f0 100%)',
            }}
          >
            <DownloadIcon sx={{ fontSize: 60, color: '#9E9E9E', mb: 2 }} />
            <Typography color="textSecondary" variant="h6">
              Select filters and click "Generate Projections" to create projection data,
              <br />
              then click "Load Report" to view the comparison.
            </Typography>
          </Paper>
        )}
      </Box>
    </Container>
  );
};

export default ProjectionComparison;
